import { h, clear } from './lib/dom.js';
import { getState } from './store.js';
import { go, goReceive, goSend, openStatusPanel, openProfileMenu, ensureDefaultKey } from './actions.js';
import { applySwUpdate } from './sw-update.js';
import { iconMarkup } from './icons/index.js';
import { renderLanding } from './screens/landing.js';
import { renderHome } from './screens/home.js';
import { renderReceive } from './screens/receive.js';
import { renderSend } from './screens/send.js';
import { renderTransfer } from './screens/transfer.js';
import { renderActivity } from './screens/activity.js';
import { renderSettings } from './screens/settings.js';
import { renderRecipients } from './screens/recipients.js';
import { renderOpenAccountModal } from './modals/openAccount.js';
import { renderAddRecipientModal } from './modals/addRecipient.js';
import { renderImportKeyModal } from './modals/importKey.js';
import { renderConfirmRevealModal } from './modals/confirmReveal.js';
import { renderStatusPanel } from './modals/statusPanel.js';
import { renderProfileMenu } from './modals/profileMenu.js';
import { renderLicenseModal } from './modals/license.js';
import { updateToast } from './toast.js';

const SCREENS = {
  home: renderHome,
  receive: renderReceive,
  move: renderSend,
  transfer: renderTransfer,
  txns: renderActivity,
  settings: renderSettings,
  recipients: renderRecipients
};

const TABS = [
  { screen: 'home', icon: 'home', label: 'Home' },
  { screen: 'receive', icon: 'receive', label: 'Receive' },
  { screen: 'move', icon: 'send', label: 'Send' },
  { screen: 'txns', icon: 'activity', label: 'Activity' },
  { screen: 'settings', icon: 'settings', label: 'Settings' }
];

function renderHeader(state) {
  const online = state.online;
  const statusIcon = online ? 'shieldCog' : 'shieldX';
  const statusColor = online ? '#1a8a4c' : '#c0392b';
  const activeProfile = state.profiles.find((p) => p.id === state.activeProfileId);
  const profileIcon = (activeProfile && activeProfile.avatar) || 'circleUserRound';
  return h('div', { class: 'header-bar' }, [
    h('div', { class: 'header-brand' }, [
      h('img', { src: '/icons/icon.svg', alt: '' }),
      h('span', {}, 'Tynti'),
      // The one cross-screen signal Settings' Testnet toggle drives directly
      // — a real network-switching confirmation flow on Send/Transfer's
      // review screens (per CLAUDE.md: "mainnet actions need a visually
      // distinct, explicit confirmation step") is a larger, separate change
      // to those screens, not something this Settings-only pass reaches.
      !state.testnetEnabled ? h('span', { class: 'mainnet-badge' }, 'MAINNET') : null
    ]),
    h('div', { class: 'header-icons' }, [
      h('button', {
        class: 'status-icon-btn',
        'aria-label': online ? 'Status: online' : 'Status: offline',
        onclick: openStatusPanel
      }, h('span', { html: iconMarkup(statusIcon, { size: 22, color: statusColor }) })),
      h('button', {
        class: 'status-icon-btn',
        'aria-label': 'Switch profile',
        onclick: openProfileMenu
      }, h('span', { html: iconMarkup(profileIcon, { size: 22, color: '#757993' }) }))
    ])
  ]);
}

function renderTabbar(activeScreen) {
  return h('div', { class: 'tabbar' }, TABS.map((tab) => {
    const active = tab.screen === activeScreen;
    const onclick = tab.screen === 'receive' ? goReceive : tab.screen === 'move' ? goSend : () => go(tab.screen);
    return h('button', { class: 'tab' + (active ? ' active' : ''), onclick }, [
      h('span', { html: iconMarkup(tab.icon, { size: 20, color: active ? '#4D4DFF' : '#757993' }) }),
      h('span', {}, tab.label)
    ]);
  }));
}

function renderUpdateBanner() {
  return h('div', { class: 'update-banner' }, [
    h('span', {}, 'New version available'),
    h('button', { class: 'btn btn-primary btn-sm', onclick: applySwUpdate }, 'Refresh')
  ]);
}

function renderWorkerBanner() {
  return h('div', { class: 'update-banner banner-warn' }, [
    h('span', {}, "Wallet features unavailable — worker failed to start"),
    h('button', { class: 'btn btn-secondary btn-sm', onclick: ensureDefaultKey }, 'Retry')
  ]);
}

function renderBannerStack(state) {
  const items = [];
  if (state.swUpdateAvailable) items.push(renderUpdateBanner());
  if (state.workerConnected === false) items.push(renderWorkerBanner());
  return items.length ? h('div', { class: 'banner-stack' }, items) : null;
}

function renderApp(state) {
  const renderScreen = SCREENS[state.screen] || renderHome;
  return h('div', { class: 'app-shell' }, [
    renderHeader(state),
    h('div', { class: 'screen-body' }, [renderScreen()]),
    renderTabbar(state.screen)
  ]);
}

const MODAL_FLAGS = ['showOpenAccount', 'showAddRecipient', 'showImportKey', 'showConfirmReveal', 'showStatusPanel', 'showProfileMenu', 'showLicense'];
let openModalFlag = null;

// Removing a focused node from the document (which clear() does on every
// render) synchronously fires a native 'blur' event on it. If that blur
// handler itself calls setState (e.g. inline validation), render() gets
// re-entered while the outer call is still mid-teardown/rebuild — clear()'s
// live child walk and the outer appendChild calls end up fighting over a
// root that a nested render() already mutated, throwing NotFoundError.
// Guarding here (rather than at each call site) protects every render path,
// not just the one that happened to surface it.
let rendering = false;
let renderQueued = false;

// Fields that must re-render on every keystroke (live search/filtering,
// unlike the setSilent text inputs elsewhere) opt in via
// data-preserve-focus="<key>". clear() below tears down and rebuilds the
// whole DOM on every render, which would otherwise steal focus and the
// caret position — and dismiss the on-screen keyboard on mobile — after
// every character typed.
function capturePreserveFocus() {
  const active = document.activeElement;
  const key = active && active.dataset ? active.dataset.preserveFocus : null;
  if (!key) return null;
  const hasSelection = 'selectionStart' in active;
  return {
    key,
    selStart: hasSelection ? active.selectionStart : null,
    selEnd: hasSelection ? active.selectionEnd : null
  };
}

function restorePreserveFocus(root, captured) {
  if (!captured) return;
  const el = root.querySelector(`[data-preserve-focus="${captured.key}"]`);
  if (!el) return;
  el.focus();
  if (captured.selStart != null && 'setSelectionRange' in el) {
    try { el.setSelectionRange(captured.selStart, captured.selEnd); } catch { /* not applicable to this input type */ }
  }
}

export function render(root) {
  if (rendering) { renderQueued = true; return; }
  rendering = true;
  try {
    const state = getState();
    if (state.theme === 'light' || state.theme === 'dark') {
      document.documentElement.setAttribute('data-theme', state.theme);
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    const preserved = capturePreserveFocus();
    clear(root);

    let main;
    if (state.screen === 'landing') main = renderLanding();
    else main = renderApp(state);
    root.appendChild(main);

    const banners = renderBannerStack(state);
    if (banners) root.appendChild(banners);

    const overlays = [
      renderOpenAccountModal(), renderAddRecipientModal(), renderImportKeyModal(),
      renderConfirmRevealModal(), renderStatusPanel(), renderProfileMenu(), renderLicenseModal()
    ].filter(Boolean);
    overlays.forEach((el) => root.appendChild(el));

    updateToast(state.toast);
    restorePreserveFocus(root, preserved);

    // Autofocus the first focusable element only on the closed->open
    // transition, not on every re-render while a modal is already open
    // (which would steal focus back from whatever the user is doing).
    const nowOpenFlag = MODAL_FLAGS.find((f) => state[f]) || null;
    if (nowOpenFlag && nowOpenFlag !== openModalFlag) {
      const el = root.querySelector('.modal-card input, .modal-card textarea, .modal-card select, .modal-card button');
      if (el) requestAnimationFrame(() => el.focus());
    }
    openModalFlag = nowOpenFlag;
  } finally {
    rendering = false;
  }
  if (renderQueued) {
    renderQueued = false;
    render(root);
  }
}
